export const MAX_FILE_BYTES = 100 * 1024 * 1024;
export const MAX_BATCH_BYTES = 500 * 1024 * 1024;
export const MAX_QUEUE_FILES = 20;

export const SUPPORTED_EXTENSIONS = new Set([
  'csv',
  'doc',
  'docx',
  'json',
  'md',
  'odp',
  'ods',
  'odt',
  'pdf',
  'ppt',
  'pptx',
  'rtf',
  'txt',
  'xls',
  'xlsx',
]);

export type FileDescriptor = Pick<File, 'name' | 'size' | 'type'>;

export type FileValidation =
  | { ok: true; baseName: string; extension: string }
  | { ok: false; reason: string };

export function splitFilename(filename: string): { baseName: string; extension: string } {
  const trimmed = filename.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) {
    return { baseName: trimmed || 'document', extension: '' };
  }
  return {
    baseName: trimmed.slice(0, dot),
    extension: trimmed.slice(dot + 1),
  };
}

export function cleanFilenameBase(value: string): string {
  const withoutControls = Array.from(value, (character) => character.charCodeAt(0) <= 31 ? ' ' : character).join('');
  return withoutControls
    .normalize('NFKC')
    .replace(/[<>:"/\\|?*]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '')
    .slice(0, 120);
}

export function destinationName(baseName: string, extension: string): string {
  const cleanBase = cleanFilenameBase(baseName) || 'document';
  return extension ? `${cleanBase}.${extension}` : cleanBase;
}

export function uniqueDestinationName(originalName: string, usedNames: Iterable<string>): string {
  const { baseName, extension } = splitFilename(originalName);
  const cleanBase = cleanFilenameBase(baseName) || 'document';
  const used = new Set(Array.from(usedNames, (name) => name.toLocaleLowerCase()));
  let candidate = destinationName(cleanBase, extension);
  let copy = 2;

  while (used.has(candidate.toLocaleLowerCase())) {
    candidate = destinationName(`${cleanBase} (${copy})`, extension);
    copy += 1;
  }
  return candidate;
}

export function validateDocument(file: FileDescriptor): FileValidation {
  const { baseName, extension } = splitFilename(file.name);
  const normalizedExtension = extension.toLocaleLowerCase();

  if (!SUPPORTED_EXTENSIONS.has(normalizedExtension)) {
    return {
      ok: false,
      reason: `${file.name || 'That file'} is not a supported document.`,
    };
  }
  if (file.size <= 0) {
    return { ok: false, reason: `${file.name} is empty.` };
  }
  if (file.size > MAX_FILE_BYTES) {
    return { ok: false, reason: `${file.name} is over the 100 MB limit.` };
  }
  if (!cleanFilenameBase(baseName)) {
    return { ok: false, reason: `${file.name} needs a usable file name.` };
  }
  return { ok: true, baseName: cleanFilenameBase(baseName), extension };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
}
